<script setup lang="ts">
/**
 * Fill a whole PARTY BLOCK from one click (MAR-176 item 2).
 *
 * Inputs that share `constraints.entitySource.role` describe one party of the instrument — the
 * trabajador's name, identification, profession, address, city. This offers the parties the active
 * client or case actually holds, and picking one fills every input in the block at once.
 *
 * **This reverses MAR-129's "suggests, never fills".** That rule was written for a single
 * identifier field, where a prefilled number is a number nobody reads and the one thing in a legal
 * document that must not be silently wrong. Both halves of the reasoning survive here, and they
 * point the other way for a block: the user makes ONE deliberate choice ("this is the trabajador"),
 * every field it wrote is listed before and after the click, each stays editable, and the whole fill
 * can be undone in one action. What made prefilling dangerous was that nobody chose it; here the
 * choice IS the interaction. Filling six fields one chip at a time was the actual reported problem.
 *
 * Renders NOTHING when the scope is global, when the fetch fails, or when there is no match — the
 * common path, and degrading to plain typed fields is correct behaviour, not an error state.
 */
import { Upload } from 'lucide-vue-next'
import { useDocumentsIndexingStore } from '~/stores/documentsIndexing'
import { useCasesStore } from '~/stores/cases'
import { foldValue, valueForTarget } from '~~/shared/entities/partyValues'
import { isBlankValue } from '~/utils/workflowFields'
import { humanizeRole } from '~/utils/workflowPartyBlocks'
import type { EntityCandidate as PartyCandidate } from '~~/shared/entities/entityCandidate'

/** One input of the block, reduced to what filling it needs. */
export interface PartyFillTarget {
  /** Key in the run form's `inputValues` bag. */
  inputKey: string
  /** Human label, so the user is told what was written rather than watching fields change. */
  label: string
  /** `name` | `idNumber` | `attribute:<canonical key>` | `<slot>.<property>` */
  field: string
  /**
   * The input's declared options, when it is a `select`. A stored attribute is whatever a document
   * printed — `SOLTERO(A)` — while the option value is the workflow's own token — `soltero/a`.Writing
   * the raw value into a select produces a control matching no option: it renders BLANK while
   * `fieldComplete` counts it filled, so the run gate would let an empty field through. The value is
   * matched onto an option instead, and skipped when nothing matches.
   */
  options?: unknown[]
}

/**
 * Which of this block's inputs are currently filled-and-collapsed.
 *
 * Emitted rather than handled here because the FIELDS are rendered by `WorkflowRunForm`, one row
 * per input; this component only knows which keys it wrote. The parent hides them, so the collapse
 * is a property of the form's layout and this stays the thing that fills a block.
 */
const emit = defineEmits<{ collapse: [role: string, inputKeys: string[]] }>()

const props = defineProps<{
  /** The role these inputs share — used only to group and to label the block. */
  role: string
  targets: PartyFillTarget[]
  /** The identifier category the block's `idNumber` field wants, when it has one. */
  idType?: string | null
  /** The entity kind (`organization` | `person`) this slot accepts, when constrained (MAR-1069). */
  kind?: string | null
  /** The run form's shared value bag; this component writes the whole block into it. */
  inputValues: Record<string, any>
}>()

const { t } = useI18n()
// The SCOPE, not the rail's payload. `useContextRail` derives its state from an async fetch, so a
// control that mounts with the page reads an empty rail and would silently offer nothing.
const webChat = useWebChatStore()

/**
 * A readable title for the block, from the template's own free-text `role` (MAR-437 follow-up).
 *
 * `role` was previously used only to GROUP inputs and to key the `collapse` emit — never shown. Every
 * block rendered the identical generic caption ("Rellenar con una parte…"), so three blocks stacked
 * in the chat card were indistinguishable until you read a candidate's fine print. `role` is free
 * text per template (`shared/templates/schema.ts`: "an acta has a presidente, a finiquito a
 * trabajador"), so this can only humanize it generically — capitalize, underscores to spaces — not
 * map it through a fixed vocabulary.
 */
const roleLabel = computed(() => humanizeRole(props.role))

/** What this block will write — the picker computes its coverage badge from exactly this. */
const pickerTargets = computed(() =>
  props.targets.map(tgt => ({ field: tgt.field, label: tgt.label, options: tgt.options })))

/** The party currently filled in, and what it wrote — the receipt, and what Undo restores. */
const applied = ref<{
  candidate: PartyCandidate
  previous: Record<string, any>
  filled: string[]
  keys: string[]
} | null>(null)

/**
 * Whether the full picker is showing, or just a one-line summary (MAR-437 follow-up).
 *
 * Collapsed by default, always — not only once a party is picked. Three blocks in a chat card each
 * showing a search box and two full candidate cards is what made the card long enough to need
 * zooming the whole page out to see; a role name and one action ("Seleccionar…") is enough until the
 * user actually wants to choose. Distinct from `collapsed` above, which hides the FILLED FIELDS
 * elsewhere in the form — this hides the PICKER itself, in this component.
 */
const pickerOpen = ref(false)

/** Every party this scope offers, from the picker's own fetch — kept here only so a block can
 *  recognize its OWN current values against them (`effectiveApplied` below). */
const allCandidates = ref<PartyCandidate[]>([])
function onCandidatesLoaded(candidates: PartyCandidate[]) {
  allCandidates.value = candidates
  hasParties.value = candidates.length > 0
  const match = derivedMatch.value
  if (match && !applied.value) {
    for (const tgt of props.targets) {
      if (!isBlankValue(props.inputValues[tgt.inputKey])) continue
      const value = valueFor(match, tgt)
      if (value) props.inputValues[tgt.inputKey] = value
    }
  }
}

/** The block's own name field, if it has one — the anchor `effectiveApplied` matches on. */
const nameTarget = computed(() => props.targets.find(tgt => tgt.field === 'name' || tgt.field.endsWith('.name')))

/**
 * A party this block's CURRENT values already match, even though nobody picked one this session.
 *
 * This is what makes the compact summary survive a card rebuild: editing a field via chat replaces
 * the whole card, which remounts this component and wipes `applied` — but the field VALUES it wrote
 * are untouched, since they live on `request.fields`, not in this component. Recognizing "these
 * values already belong to a known party" from the name alone (not from `applied`) means the summary
 * reappears immediately instead of the form silently reverting to "pick someone" for a block that
 * was already filled.
 *
 * Anchored on the NAME specifically, never another field: `sexo`/`nacionalidad`/etc. are shared by
 * many parties and would false-match the first candidate with the same value. The name is the one
 * fact a party block is actually identified by.
 */
const derivedMatch = computed(() => {
  if (!nameTarget.value) return null
  const currentName = String(props.inputValues[nameTarget.value.inputKey] ?? '').trim()
  if (!currentName) return null
  const folded = foldValue(currentName)
  return allCandidates.value.find(c => c.name === currentName || (folded && foldValue(c.name) === folded)) ?? null
})

/** What the compact summary and the "Deshacer" gate read — an explicit pick from this session where
 *  there is one, else a match recognized from values already on the block. */
const effectiveApplied = computed(() => {
  if (applied.value) return applied.value
  const match = derivedMatch.value
  if (!match) return null
  const filled: string[] = []
  const keys: string[] = []
  for (const tgt of props.targets) {
    if (props.inputValues[tgt.inputKey] && valueFor(match, tgt)) {
      filled.push(tgt.label)
      keys.push(tgt.inputKey)
    }
  }
  return { candidate: match, previous: {}, filled, keys }
})

/**
 * Filled fields collapse by default.
 *
 * A block fills eight of eleven inputs, and the three that still need typing were then buried among
 * eight that read as done — the user had to scan every row to find the work. Hiding what is settled
 * leaves only what is outstanding, which is the whole reason to fill a block in one click.
 *
 * Collapsed is not disabled: `Editar` brings them straight back, and every value stays in
 * `inputValues` the entire time, so nothing about the run depends on whether they are on screen.
 */
const collapsed = ref(true)

function publishCollapse() {
  emit('collapse', props.role, collapsed.value && applied.value ? applied.value.keys : [])
}

/**
 * The value to actually write into one input — delegated to `shared/entities/partyValues.ts`.
 *
 * Shared rather than local since MAR-179: a batch row resolves to an entity and its autofillable
 * columns must fill with exactly what a single run would have written here. Two implementations
 * would eventually disagree, and the disagreement would surface as a batch of thirty documents
 * differing from the one the reviewer checked by hand.
 */
function valueFor(c: PartyCandidate, tgt: PartyFillTarget): string {
  return valueForTarget(c, tgt)
}

/**
 * Fill the block.
 *
 * Only fields the candidate actually HAS are written. A party with no recorded profession leaves
 * that input exactly as the user left it — overwriting a typed value with a blank would be the
 * autofill destroying work, which is worse than not filling at all.
 */
function apply(c: PartyCandidate) {
  const previous: Record<string, any> = {}
  const filled: string[] = []
  const keys: string[] = []
  for (const tgt of props.targets) {
    const value = valueFor(c, tgt)
    if (!value) continue
    previous[tgt.inputKey] = props.inputValues[tgt.inputKey]
    props.inputValues[tgt.inputKey] = value
    filled.push(tgt.label)
    keys.push(tgt.inputKey)
  }
  applied.value = { candidate: c, previous, filled, keys }
  collapsed.value = true
  pickerOpen.value = false
  publishCollapse()
}

function toggleCollapsed() {
  collapsed.value = !collapsed.value
  publishCollapse()
}

/** Put back exactly what was there before the fill — including a field the user had typed. */
function undo() {
  if (!applied.value) return
  for (const [key, value] of Object.entries(applied.value.previous)) {
    props.inputValues[key] = value
  }
  applied.value = null
  // Undo restores the fields AND reveals them: leaving them hidden after taking the values back
  // would hide inputs that are now empty and required.
  collapsed.value = true
  publishCollapse()
}

/**
 * Upload a document (a DUI, a passport) without leaving the form.
 *
 * **No upload state lives here.** `documentsIndexing.upload()` mints the jobs, owns the state
 * machine, parses quota errors and calls `activity.reveal()` — which opens the Activity Center, the
 * one place that reports everything Marcus is still doing. Adding a progress bar to this component
 * would be a second, worse copy of that, out of view the moment the user scrolls.
 *
 * The candidate list refreshes when the job SETTLES rather than when the upload returns: the party
 * does not exist until extraction has read the document, so refetching on the response would show
 * the old list and look like the upload did nothing.
 */
const indexing = useDocumentsIndexingStore()
const casesStore = useCasesStore()
const fileInput = ref<HTMLInputElement | null>(null)
const linking = ref(false)

/** Only offered where an uploaded document can actually BECOME a party of the active scope. Under a
 *  case that is `case_documents` (the candidate query joins through it); a client resolves its
 *  parties from `client_dependencies`, which an upload does not create — offering it there would
 *  process the file and then silently show no new party. */
const canUpload = computed(() => webChat.activeScope.kind === 'case')

async function onFilesPicked(event: Event) {
  const input = event.target as HTMLInputElement
  const files = [...(input.files ?? [])]
  input.value = ''
  if (!files.length) return

  linking.value = true
  try {
    const created = await indexing.upload(files)
    const scope = webChat.activeScope
    if (scope.kind === 'case') {
      for (const item of created) {
        if (!item?.id) continue
        // Reported, not swallowed: a file that uploaded but never linked looks exactly like a
        // success while producing no party — the failure `useCaseDocuments` documents.
        try {
          await casesStore.addCaseDocument(scope.id, item.id)
        } catch (err) {
          console.error('[marcus:party-fill] uploaded but not linked to the case:', item.id, err)
        }
      }
    }
  } finally {
    linking.value = false
  }
}

/** The picker owns the candidate list, so a settled upload refreshes it through there. */
const picker = ref<{ reload: () => Promise<void> } | null>(null)
/** Set from the picker's `loaded` emit — see the note there on why this is not read off the child. */
const hasParties = ref(false)

// One subscription for the component's lifetime; the returned unsubscribe is what keeps a form that
// is opened and closed repeatedly from stacking listeners.
onMounted(() => {
  const off = indexing.onSettled(() => { void picker.value?.reload() })
  onUnmounted(off)
})
</script>

<template>
  <div
    v-show="hasParties || canUpload"
    class="mb-3 rounded-lg border border-dashed border-border bg-accent-lightest px-3 py-2"
  >
    <!-- Compact by default, always — not only once a party is picked (`pickerOpen`'s own comment).
         Role name, current status, one or two short actions: this is the whole point of the
         redesign, three of these stacked used to cost ~500px and forced zooming the page out just
         to see the rest of the card.

         `v-show`, not `v-if`/`v-else` with the expanded branch below (MAR-437 follow-up): the
         picker has to stay MOUNTED even while collapsed, or its candidate list never loads until
         the user opens it — and `derivedMatch`/`effectiveApplied` need that list to recognize a
         value set by a DIFFERENT instance of this same component (the chat card and the context
         rail each mount their own). Without this, a pick made on one surface shows correctly there
         but reads as "unassigned" on the other until its own picker happens to be opened once. -->
    <div v-show="!pickerOpen" class="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs">
      <span class="shrink-0 font-semibold text-foreground">{{ roleLabel }}</span>
      <template v-if="effectiveApplied">
        <span class="min-w-0 flex-1 truncate text-muted-foreground">
          {{ effectiveApplied.candidate.name }}<template v-if="effectiveApplied.filled.length"> · {{ t('workflowRun.partyFill.fieldCount', { count: effectiveApplied.filled.length }) }}</template>
        </span>
        <!-- Only meaningful for the fuller run form, which renders the filled fields elsewhere and
             uses `collapse` to hide/show them — the chat card has nothing listening for it, so this
             is a harmless no-op there. -->
        <button
          type="button"
          class="shrink-0 font-medium text-muted-foreground hover:text-foreground hover:underline"
          @click="toggleCollapsed"
        >
          {{ collapsed ? t('workflowRun.partyFill.edit') : t('workflowRun.partyFill.hideFilled') }}
        </button>
        <button
          type="button"
          class="shrink-0 font-medium text-accent-ink hover:underline"
          @click="pickerOpen = true"
        >
          {{ t('workflowRun.partyFill.change') }}
        </button>
        <!-- Only once THIS session actually applied a fill: a match merely recognized from values
             already on the block (`derivedMatch`) has no "before" of its own to restore. -->
        <button
          v-if="applied"
          type="button"
          class="shrink-0 font-medium text-muted-foreground hover:text-foreground hover:underline"
          @click="undo"
        >
          {{ t('workflowRun.partyFill.undo') }}
        </button>
      </template>
      <template v-else>
        <span class="min-w-0 flex-1 text-muted-foreground">{{ t('workflowRun.partyFill.unassigned') }}</span>
        <button
          type="button"
          class="shrink-0 font-medium text-accent-ink hover:underline"
          @click="pickerOpen = true"
        >
          {{ t('workflowRun.partyFill.select') }}
        </button>
      </template>
    </div>

    <!-- Expanded: today's picker, unchanged, plus one way back to the compact row. Always mounted
         (see the `v-show` note above) — only its VISIBILITY toggles with `pickerOpen`. -->
    <div v-show="pickerOpen" class="text-xs">
      <div class="mb-2 flex items-center justify-between">
        <span class="font-semibold text-foreground">{{ roleLabel }}</span>
        <button
          type="button"
          class="text-muted-foreground hover:text-foreground"
          @click="pickerOpen = false"
        >
          {{ t('common.close') }}
        </button>
      </div>

      <div class="flex items-center gap-2">
        <!-- Progress is NOT shown here: `indexing.upload()` reveals the Activity Center, which is
             the one place that reports everything still running. -->
        <button
          v-if="canUpload"
          type="button"
          class="ml-auto inline-flex shrink-0 items-center gap-1 text-muted-foreground transition-colors hover:text-foreground disabled:opacity-50"
          :disabled="linking"
          :title="t('workflowRun.partyFill.uploadHint')"
          @click="fileInput?.click()"
        >
          <Upload class="h-3 w-3" />
          {{ t('workflowRun.partyFill.upload') }}
        </button>
        <input ref="fileInput" type="file" multiple class="hidden" @change="onFilesPicked">
      </div>

      <!-- The list itself is shared with the batch table, so the two can never offer different
           parties or rank them differently. See `EntityPartyPicker`. -->
      <WorkflowEntityPartyPicker
        ref="picker"
        :id-type="idType"
        :kind="kind"
        :targets="pickerTargets"
        @select="apply"
        @loaded="onCandidatesLoaded"
      />
    </div>
  </div>
</template>
